import { forwardRef, ReactNode } from 'react';
import { Button as AntButton } from 'antd';
import type { ButtonProps as AntButtonProps } from 'antd';

type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  leftIcon?: ReactNode;
  fullWidth?: boolean;
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  onClick?: React.MouseEventHandler<HTMLElement>;
  children?: ReactNode;
  className?: string;
  style?: React.CSSProperties;
  icon?: ReactNode;
}

const sizeMap: Record<ButtonSize, AntButtonProps['size']> = {
  sm: 'small',
  md: 'middle',
  lg: 'large',
};

function getAntProps(variant: ButtonVariant): { type: AntButtonProps['type']; danger?: boolean } {
  switch (variant) {
    case 'primary': return { type: 'primary' };
    case 'secondary': return { type: 'primary' };
    case 'outline': return { type: 'default' };
    case 'ghost': return { type: 'text' };
    case 'danger': return { type: 'primary', danger: true };
  }
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      variant = 'primary',
      size = 'md',
      loading = false,
      leftIcon,
      fullWidth = false,
      disabled,
      type = 'button',
      icon,
      ...props
    },
    ref
  ) => {
    const { type: antType, danger } = getAntProps(variant);

    return (
      <AntButton
        ref={ref}
        htmlType={type}
        type={antType}
        danger={danger}
        size={sizeMap[size]}
        block={fullWidth}
        disabled={disabled}
        loading={loading}
        icon={icon || leftIcon}
        {...props}
      >
        {children}
      </AntButton>
    );
  }
);

Button.displayName = 'Button';
